import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import Sidebar from '../../components/Layout/Sidebar'
import Header from '../../components/Layout/Header'
import Card from '../../components/UI/Card'
import Button from '../../components/UI/Button'
import {UserPlus, Phone, Lock, Wallet, AlertCircle, CheckCircle} from 'lucide-react'
import {api} from '../../services/api'

const AddUser: React.FC = () => {
    const navigate = useNavigate()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    // 👇 استبدلنا message القديم بهذا
    const [toastMessage, setToastMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)

    const [formData, setFormData] = useState({
        phone: '',
        password: '',
        wallet: 'alfa',
        role: 'user'
    })

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target
        setFormData(prev => ({...prev, [name]: value}))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setToastMessage(null)

        try {
            if (!formData.phone || !formData.password) {
                setToastMessage({
                    type: 'error',
                    text: 'يرجى إدخال رقم الهاتف وكلمة المرور'
                })
                setTimeout(() => setToastMessage(null), 3000)
                setLoading(false)
                return
            }

            let phoneNumber = formData.phone
            if (!phoneNumber.startsWith('961')) {
                phoneNumber = '961' + phoneNumber.replace(/^0+/, '')
            }

            const response = await api.post('/api/admin/create', {
                phone_number: phoneNumber,
                password: formData.password,
                wallet: formData.wallet,
                role: formData.role
            })

            if (response.data.success) {
                // 👇 رسالة نجاح
                setToastMessage({
                    type: 'success',
                    text: 'تم إنشاء المستخدم بنجاح'
                })

                // تصفير الفورم
                setFormData({phone: '', password: '', wallet: 'alfa', role: 'user'})

                // بعد 2 ثانية نروح لصفحة المستخدمين
                setTimeout(() => {
                    navigate('/users')
                }, 2000)
            } else {
                // 👇 رسالة خطأ
                setToastMessage({
                    type: 'error',
                    text: response.data.error || 'فشل إنشاء المستخدم'
                })
                setTimeout(() => setToastMessage(null), 3000)
            }
        } catch (error: any) {
            // 👇 رسالة خطأ
            setToastMessage({
                type: 'error',
                text: error.response?.data?.error || 'حدث خطأ في الاتصال'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } finally {
            setLoading(false)
        }
    }

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

    return (
        <div className="min-h-screen bg-gray-50">
            {/* 👇 Toast Message */}
            {toastMessage && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
                    <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] justify-center ${toastMessage.type === 'success'
                        ? 'bg-green-500 text-white'
                        : 'bg-red-500 text-white'
                        }`}>
                        {toastMessage.type === 'success'
                            ? <CheckCircle className="w-5 h-5" />
                            : <AlertCircle className="w-5 h-5" />
                        }
                        <span className="font-medium">{toastMessage.text}</span>
                    </div>
                </div>
            )}

            <Header toggleSidebar={toggleSidebar} />
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isMobile={true} />
            <div className="hidden lg:block"><Sidebar isMobile={false} /></div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <UserPlus className="w-6 h-6 text-blue-600" />
                        <h1 className="text-2xl font-bold text-gray-800">إضافة مستخدم جديد</h1>
                    </div>

                    <Card>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    رقم الهاتف <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="96170123456"
                                        dir="rtl"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    كلمة المرور <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="********"
                                        dir="rtl"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    المحفظة
                                </label>
                                <div className="relative">
                                    <Wallet className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <select
                                        name="wallet"
                                        value={formData.wallet}
                                        onChange={handleChange}
                                        className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                                        dir="rtl">
                                        <option value="alfa">Alfa</option>
                                        <option value="touch">Touch</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    نوع المستخدم
                                </label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="role"
                                            value="user"
                                            checked={formData.role === 'user'}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600"
                                        />
                                        <span>مستخدم</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="role"
                                            value="admin"
                                            checked={formData.role === 'admin'}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600"
                                        />
                                        <span>مدير</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <Button type="submit" loading={loading}>
                                    إنشاء المستخدم
                                </Button>
                                <Button type="button" variant="outline" onClick={() => navigate('/users')}>
                                    إلغاء
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>
            </main>
        </div>
    )
}

export default AddUser